import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircle2 } from 'lucide-react';
import { Modal, Button, Select } from '../../../components';
import { qualityService } from '../../../services';
import { FieldLabel, Notice, inputClass, isoPlusDays, todayISO, formatDate, errorMessage } from './shared';

/**
 * Raise, update or complete a follow-up action.
 *
 * A completed action opens read-only. Its completion note is the record of
 * what was actually done about a finding, and quietly letting it be edited
 * afterwards would make that record worth less than the tick beside it.
 * Reopening is the way back — and it KEEPS the old note, because "we thought
 * this was done, here is what was done, and it wasn't enough" is the useful
 * history.
 */
const ActionFormModal = ({ open, onClose, meta, action, audit, question, onSaved }) => {
  const editing = Boolean(action);
  const completed = action?.status === 'done';

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      title: action?.title || '',
      owner_id: String(action?.owner_id || audit?.owner_id || meta?.staff?.[0]?.id || ''),
      due_date: String(action?.due_date || isoPlusDays(7)).slice(0, 10),
      status: action?.status || 'open',
      completion_note: action?.completion_note || '',
      audit_id: String(action?.audit_id || audit?.id || ''),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action?.id]);

  const staffOptions = useMemo(
    () => (meta?.staff || []).map((s) => ({ value: String(s.id), label: s.name })),
    [meta],
  );

  if (!open || !form) return null;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const reopen = async () => {
    setSaving(true);
    try {
      const response = await qualityService.reopenAction(action.id);
      toast.success(response.message || 'Action reopened.');
      onSaved(response.data);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        owner_id: form.owner_id ? Number(form.owner_id) : null,
        due_date: form.due_date,
      };
      const response = editing
        ? await qualityService.updateAction(action.id, {
          ...payload,
          status: form.status,
          completion_note: form.completion_note,
        })
        : await qualityService.createAction({
          ...payload,
          audit_id: form.audit_id ? Number(form.audit_id) : null,
          question_id: question?.id || null,
        });

      toast.success(response.message || 'Saved.');
      onSaved(response.data);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (completed) {
    return (
      <Modal isOpen={open} onClose={onClose} title="Completed action" maxWidth="max-w-lg">
        <div className="space-y-4">
          <div>
            <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">{action.title}</h3>
            <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1">
              {action.owner_name || 'Unassigned'} · Due {formatDate(action.due_date)}
            </p>
          </div>

          <Notice tone="green">
            <span className="inline-flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              Completed by {action.completed_by_name || 'a manager'} on {formatDate(action.completed_at)}
            </span>
          </Notice>

          <div>
            <FieldLabel>What was done</FieldLabel>
            <p className="text-[13px] leading-relaxed text-[#334155] dark:text-gray-300 whitespace-pre-wrap">
              {action.completion_note || 'No completion note was recorded.'}
            </p>
          </div>

          {error && <Notice tone="red">{error}</Notice>}

          <div className="flex items-center justify-between gap-2.5 pt-2">
            <Button variant="secondary" onClick={reopen} loading={saving}>Reopen action</Button>
            <Button variant="primary" onClick={onClose}>Done</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={editing ? 'Update action' : 'Add follow-up action'}
      maxWidth="max-w-lg"
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1">
          {audit ? `Linked to ${audit.title}` : 'Track a general follow-up from your audits.'}
        </p>

        {error && <Notice tone="red">{error}</Notice>}

        {question && (
          <Notice tone="amber">
            <span className="font-semibold block mb-1">Finding</span>
            {question.note || question.text}
          </Notice>
        )}

        <div>
          <FieldLabel required>What needs to be done?</FieldLabel>
          <textarea
            className={inputClass}
            rows={3}
            maxLength={400}
            required
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Describe a clear, practical action…"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel required>Action owner</FieldLabel>
            <Select
              value={form.owner_id}
              onChange={(v) => set('owner_id', v)}
              options={staffOptions}
              placeholder="Choose a staff member"
            />
          </div>
          <div>
            <FieldLabel required>Due date</FieldLabel>
            <input
              type="date"
              className={inputClass}
              value={form.due_date}
              onChange={(e) => set('due_date', e.target.value)}
              min={editing ? undefined : todayISO()}
              required
            />
          </div>
        </div>

        {editing && (
          <>
            <div>
              <FieldLabel>Status</FieldLabel>
              <Select
                value={form.status}
                onChange={(v) => set('status', v)}
                options={[{ value: 'open', label: 'Open' }, { value: 'done', label: 'Completed' }]}
              />
            </div>
            <div>
              <FieldLabel required={form.status === 'done'}>Update / completion note</FieldLabel>
              <textarea
                className={inputClass}
                rows={4}
                value={form.completion_note}
                onChange={(e) => set('completion_note', e.target.value)}
                placeholder="Describe what was done. Required when completing an action."
              />
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2.5 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={saving}>
            {editing ? 'Save update' : 'Add action'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ActionFormModal;
